// API Configuration Service
// Handles environment variables and API endpoint configuration

export interface ApiConfig {
  baseUrl: string;
  sopApiUrl: string;
  region: string;
  accountId: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
}

export interface EnvironmentConfig {
  isDevelopment: boolean;
  isProduction: boolean;
  stage: string;
}

class ConfigService {
  private static instance: ConfigService;
  private config: ApiConfig;
  private environment: EnvironmentConfig;

  private constructor() {
    this.environment = this.loadEnvironmentConfig();
    this.config = this.loadApiConfig();
    this.validateConfig();
  }

  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  private loadEnvironmentConfig(): EnvironmentConfig {
    const nodeEnv = process.env.NODE_ENV || 'development';
    const stage = process.env.REACT_APP_STAGE || 'dev';

    return {
      isDevelopment: nodeEnv === 'development',
      isProduction: nodeEnv === 'production',
      stage
    };
  }

  private loadApiConfig(): ApiConfig {
    // Get API URL from environment variables
    const baseUrl = process.env.REACT_APP_API_URL || this.getDefaultApiUrl();
    const sopApiUrl = process.env.REACT_APP_SOP_API_URL || this.getDefaultSopApiUrl();
    
    // Get AWS configuration
    const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const accountId = process.env.REACT_APP_AWS_ACCOUNT_ID || '944584227485';

    // Configuration for timeouts and retries
    const timeout = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
    const retryAttempts = parseInt(process.env.REACT_APP_RETRY_ATTEMPTS || '3', 10);
    const retryDelay = parseInt(process.env.REACT_APP_RETRY_DELAY || '1000', 10);

    return {
      baseUrl,
      sopApiUrl,
      region,
      accountId,
      timeout,
      retryAttempts,
      retryDelay
    };
  }

  private getDefaultApiUrl(): string {
    // Use the deployed API Gateway URL
    const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const stage = this.environment.stage;
    
    // Deployed API Gateway URL from DEPLOYMENT_SUMMARY.md
    return `https://lzapjgikij.execute-api.${region}.amazonaws.com/${stage}`;
  }

  private getDefaultSopApiUrl(): string {
    // Use the deployed S&OP API Gateway URL
    const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const stage = this.environment.stage;
    
    // Deployed S&OP API Gateway URL from deployment
    return `https://to8rc0kieg.execute-api.${region}.amazonaws.com/${stage}`;
  }

  private validateConfig(): void {
    const errors: string[] = [];

    // Validate required configuration
    if (!this.config.baseUrl || this.config.baseUrl.includes('your-api-gateway-url')) {
      errors.push('REACT_APP_API_URL is not properly configured. Please update with your actual API Gateway URL.');
    }

    if (!this.config.region) {
      errors.push('REACT_APP_AWS_REGION is required');
    }

    if (!this.config.accountId) {
      errors.push('REACT_APP_AWS_ACCOUNT_ID is required');
    }

    if (this.config.timeout < 1000 || this.config.timeout > 300000) {
      errors.push('API timeout must be between 1000ms and 300000ms');
    }

    if (this.config.retryAttempts < 0 || this.config.retryAttempts > 10) {
      errors.push('Retry attempts must be between 0 and 10');
    }

    if (errors.length > 0) {
      console.warn('Configuration warnings:', errors);
      
      // In development, show warnings but don't block
      if (this.environment.isProduction) {
        throw new Error(`Configuration errors: ${errors.join(', ')}`);
      }
    }
  }

  public getApiConfig(): ApiConfig {
    return { ...this.config };
  }

  public getEnvironment(): EnvironmentConfig {
    return { ...this.environment };
  }

  public getApiUrl(path: string = ''): string {
    const baseUrl = this.config.baseUrl.replace(/\/$/, ''); // Remove trailing slash
    const cleanPath = path.replace(/^\//, ''); // Remove leading slash
    return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
  }

  public getSopApiUrl(path: string = ''): string {
    const baseUrl = this.config.sopApiUrl.replace(/\/$/, ''); // Remove trailing slash
    const cleanPath = path.replace(/^\//, ''); // Remove leading slash
    return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
  }

  public isConfigured(): boolean {
    return !this.config.baseUrl.includes('your-api-gateway-url') && 
           this.config.baseUrl.includes('lzapjgikij.execute-api');
  }

  public getConfigurationStatus(): {
    configured: boolean;
    warnings: string[];
    environment: string;
  } {
    const warnings: string[] = [];
    
    if (!this.isConfigured()) {
      warnings.push('API URL not configured - using placeholder URL');
    }

    if (this.environment.isDevelopment) {
      warnings.push('Running in development mode');
    }

    return {
      configured: this.isConfigured(),
      warnings,
      environment: this.environment.stage
    };
  }
}

// Export singleton instance
export const configService = ConfigService.getInstance();

// Export configuration for direct access
export const apiConfig = configService.getApiConfig();
export const environmentConfig = configService.getEnvironment();

// Helper functions
export const getApiUrl = (path: string = '') => configService.getApiUrl(path);
export const getSopApiUrl = (path: string = '') => configService.getSopApiUrl(path);
export const isConfigured = () => configService.isConfigured();
export const getConfigurationStatus = () => configService.getConfigurationStatus();